import moment from 'moment';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, output } from "@angular/core";
import { Calendar, CalendarModule } from 'primeng/calendar';
import { BaseEnumTextBoxComponent } from '../base-enum-text-box/base-enum-text-box.component';
import { TimeZoneData, TimeZoneInterface } from '@nts/std/timezone';
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { BaseDateMaskTextBoxComponent, DateMaskSettings } from '../base-date-mask-text-box/base-date-mask-text-box.component';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { PopperHelper } from '@nts/std/utility';
import { BehaviorSubject } from 'rxjs';
import { UICommandInterface } from '@nts/std/interfaces';


@Component({
  selector: 'nts-base-date-text-box',
  templateUrl: './base-date-text-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./base-date-text-box.component.scss'],
  standalone: true,
  imports: [
    NgxPopperjsModule,
    CalendarModule,
    FormsModule,
    BaseEnumTextBoxComponent,
    NgFor,
    NgClass,
    AsyncPipe,
    BaseDateMaskTextBoxComponent,
    NgIf
  ]
})
export class BaseDateTextBoxComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() dateTabIndex?: number;
  @Input() valueDate: Date = null;
  @Input() dateFormat = null;
  @Input() appendTo: string | HTMLDivElement = 'body';
  @Input() isRequired = false;
  @Input() isNullable = true;
  @Input() isDisabled = false;
  @Input() errorList: string[] = [];
  @Input() showErrorTooltip = true;
  @Input() showErrorBorder = true;
  @Input() customCommandList: UICommandInterface[] = [];
  @Input() showTime = false;
  @Input() showButtonBar = false;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() showOnFocus = false;
  @Input() maskSettings: DateMaskSettings;
  @Input() showIcon = true;
  @Input() hideOnDateTimeSelect = true;
  @Input() selectOtherMonths = true;
  @Input() yearNavigator = true;
  @Input() monthNavigator = true;
  @Input() title = '';
  @Input() defaultDate = moment().startOf('day').toDate();
  @Input() initialChar: string | null = null;
  @Input() listenClickOutside = false;
  @Input() timeZoneTabIndex!: number;
  @Input() showOffset = false;
  @Input() showTimeZone = false;
  @Input() valueTimeZone: TimeZoneData | null = null;
  @Input() timeZoneOptions: Array<TimeZoneInterface>;
  @Input() timeZonePlaceholder: string;
  @Input() defaultBorderColor: string = null;
  @Input() activeBorderColor: string = null;
  @Input() hoverBorderColor: string = null;

  readonly onDateValueChange = output<Date>();
  readonly onDateFocus = output<Event>();
  readonly onCalendarShow = output<any>();
  readonly onDateBlur = output<Date>();
  readonly onDateSelect = output<Date>();
  readonly onFinishEditing = output<void>();
  readonly onTimeZoneChange = output<TimeZoneData>();
  readonly onTimeZoneBlur = output<void>();
  readonly keyDown = output<Event>();
  readonly viewReady = output<void>();
  isViewReady = false;

  @ViewChild('dateMaskBox', { static: true }) dateMaskBox: BaseDateMaskTextBoxComponent;
  @ViewChild('calendar', { static: true }) calendar: Calendar;
  @ViewChild('timeZoneCombo', { static: true }) baseTimeZoneCombo?: BaseEnumTextBoxComponent;
  @ViewChild('dateContainer', { static: true }) dateContainer?: ElementRef;
  @ViewChild(NgxPopperjsDirective, { static: true }) popperError?: NgxPopperjsDirective;

  //originOnUserInput;
  mask = null;
  ngxPopperjsTriggers = NgxPopperjsTriggers;
  ngxPopperjsPlacements = NgxPopperjsPlacements;
  overrideBorderColor: string = null;
  timezonOptions$ = new BehaviorSubject<Array<TimeZoneInterface>>([]);

  private documentClickListener: any;
  private destroyed = false;

  constructor(
    private el: ElementRef,
    private zone: NgZone,
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {

    if (!this.dateFormat) {
      const inputFormat = moment().localeData().longDateFormat('L').toLowerCase();
      this.dateFormat = inputFormat.substring(0, inputFormat.length - 2);
    }

    this.initMaskSettings(this.isNullable);
    this.timezonOptions$.next(this.timeZoneOptions);
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['maskSettings'] && changes['maskSettings'].currentValue) {
      this.initMaskSettings(this.isNullable);
    }

    if (changes['defaultBorderColor'] || changes['activeBorderColor'] || changes['hoverBorderColor']) {
      this.handleOverridesColors();
    }

    if (changes['errorList']) {
      this.checkPopper();
    }

    if (changes['timeZoneOptions']) {
      this.timezonOptions$.next(this.timeZoneOptions ?? []);
    }
  }

  ngAfterViewInit(): void {
    this.bindDocumentClickListener();
    this.handleOverridesColors();

    queueMicrotask(() => {
      if (!this.destroyed) {
        this.emitViewReady();
      }
    });
  }

  private emitViewReady(): void {
    this.isViewReady = true;
    this.viewReady.emit();
  }

  handleOverridesColors() {
    if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
      // devono essere impostate tutte e tre le variabili
      return;
    }

    this.overrideBorderColor = this.defaultBorderColor;

    const button = this.calendar.el.nativeElement.querySelector(".p-datepicker-trigger");
    if (button) {
      button.style.borderColor = this.overrideBorderColor;
    }
  }

  calendarDateValueChange(value: Date) {
    //if (moment(this.valueDate, this.dateFormat, true).isValid()){
    this.valueDate = value;
    //this.onDateValueChange.emit(value)
    // }
  }

  inputDateValueChange(value: Date) {
    //if (moment(this.valueDate, this.dateFormat, true).isValid()){
    this.valueDate = value;
    this.onDateValueChange.emit(value);
    //}
  }

  // Date
  get input(): HTMLInputElement {
    return this.dateMaskBox.dateBox.nativeElement;
  }

  protected initMaskSettings(nullable: boolean): any {

    let inputFormat = moment().localeData().longDateFormat('L');
    if (this.showTime === true) {
      inputFormat += ' HH:mm';
    }

    this.maskSettings = {
      format: inputFormat,
      min: this.minDate,
      max: this.maxDate,
      showTime: this.showTime,
      nullable: this.isNullable
    };
  }

  onSelect($event: Date): void {
    this.onDateSelect.emit($event);
    setTimeout(() =>
      this.input.focus()
    );
  }

  onClickOutside($event): void {
    if (this.listenClickOutside) {
      this.onDateValueChange.emit(this.valueDate);
      this.onFinishEditing.emit();
    }
  }

  onBlur(event: Date): void {
    this.popperError?.hide();
    this.valueDate = event;
    this.onDateBlur.emit(event);
  }

  onInput(event) {
    if (this.calendar.overlayVisible) {
      this.calendar.hideOverlay();
    }
  }

  onClose(event): void {
    if (!this.documentClickListener && this.listenClickOutside) {
      this.bindDocumentClickListener();
    }
  }

  getValueDate(): Date {
    return moment(this.valueDate, this.dateFormat, true).isValid() ? this.valueDate : null;
  }

  onShow(event: any): void {
    this.unbindDocumentClickListener();
    this.onCalendarShow.emit(event);
  }

  timeZoneChange($event): void {
    this.valueTimeZone = $event.value;
    this.onTimeZoneChange.emit(this.valueTimeZone);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.unbindDocumentClickListener();
  }

  private bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

        this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.zone.run(() => {
              this.onFinishEditing.emit();

              this.cd.markForCheck();
            });
          }

        });
      });
    }
  }

  private isOutsideClicked(event: Event) {
    return !this.dateContainer?.nativeElement.contains(event.target);
    //return !(this.calendar.el.nativeElement.isSameNode(event.target) || this.isDatePickerClicked(event) || this.isDropDownClicked(event) ||
    //    this.calendar.el.nativeElement.contains(event.target));
  }

  private unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  private isDatePickerClicked(event: Event) {
    return this.calendar.el.nativeElement.contains(event.target) || (this.calendar.overlay && this.calendar.overlay.contains(<Node>event.target));
  }

  private isDropDownClicked(event: Event) {
    return this.baseTimeZoneCombo ? this.baseTimeZoneCombo.combo.el.nativeElement.contains(event.target) : false;
  }

  private checkPopper() {
    if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
      PopperHelper.hide(this.popperError);
    }
    // if (this.popperInfo && this.errorList?.length > 0) {
    //     PopperHelper.hide(this.popperInfo);
    // }
  }
}
